import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

// ─── Public pages (static imports) ───────────────────────────────────────────
import HomePage from "@/pages/public/HomePage";
import PublicRoomsPage from "@/pages/public/RoomsPage";
import RoomDetailPage from "@/pages/public/RoomDetailPage";
import NotFoundPage from "@/pages/public/NotFoundPage";

// ─── Booking pages (self-contained, no layout wrapper) ───────────────────────
import BookingPage from "@/pages/public/BookingPage";
import BookingConfirmationPage from "@/pages/public/BookingConfirmationPage";
import BookingLookupPage from "@/pages/public/BookingLookupPage";

// ─── Public pages (lazy) ─────────────────────────────────────────────────────
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const BlogPage = lazy(() => import("@/pages/public/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/public/BlogPostPage"));
const GalleryPage = lazy(() => import("@/pages/public/GalleryPage"));
const DiningPage = lazy(() => import("@/pages/public/DiningPage"));
const ExperiencesPage = lazy(() => import("@/pages/public/ExperiencesPage"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));
const TermsPage = lazy(() =>
  import("@/pages/public/LegalPages").then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import("@/pages/public/LegalPages").then((m) => ({ default: m.PrivacyPage })),
);
const CancellationPage = lazy(() =>
  import("@/pages/public/LegalPages").then((m) => ({
    default: m.CancellationPage,
  })),
);

// ─── Admin pages (lazy) ───────────────────────────────────────────────────────
const LoginPage = lazy(() => import("@/pages/admin/LoginPage"));
const DashboardPage = lazy(() => import("@/pages/admin/DashboardPage"));
const RoomTypesPage = lazy(() => import("@/pages/admin/RoomTypesPage"));
const RoomsPage = lazy(() => import("@/pages/admin/RoomsPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const ContentPage = lazy(() => import("@/pages/admin/ContentPage"));
const BookingsPage = lazy(() => import("@/pages/admin/BookingsPage"));
const CalendarPage = lazy(() => import("@/pages/admin/CalendarPage"));
const GuestsPage = lazy(() => import("@/pages/admin/GuestsPage"));
const ReportsPage = lazy(() => import("@/pages/admin/ReportsPage"));
const EnquiriesPage = lazy(() => import("@/pages/admin/EnquiriesPage"));

// ─── Suspense fallback ────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-metro-green-100 border-t-metro-green-800" />
    </div>
  );
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ── Public (with header + footer) ────────────────────────────────── */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/rooms" element={<PublicRoomsPage />} />
          <Route path="/rooms/:slug" element={<RoomDetailPage />} />
          <Route path="/dining" element={<DiningPage />} />
          <Route path="/experiences" element={<ExperiencesPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cancellation" element={<CancellationPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* ── Booking (self-contained) ──────────────────────────────────────── */}
        <Route path="/book" element={<BookingPage />} />
        <Route
          path="/booking/confirmation"
          element={<BookingConfirmationPage />}
        />
        <Route path="/booking/lookup" element={<BookingLookupPage />} />

        {/* ── Admin: login (no shell) ───────────────────────────────────────── */}
        <Route path="/admin/login" element={<LoginPage />} />

        {/* ── Admin: protected shell ────────────────────────────────────────── */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<DashboardPage />} />
            <Route path="/admin/calendar" element={<CalendarPage />} />
            <Route path="/admin/bookings" element={<BookingsPage />} />
            <Route path="/admin/rooms" element={<RoomsPage />} />
            <Route path="/admin/rooms/types" element={<RoomTypesPage />} />
            <Route path="/admin/guests" element={<GuestsPage />} />
            <Route path="/admin/enquiries" element={<EnquiriesPage />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
            <Route path="/admin/content" element={<ContentPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}